import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withRouter } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { injectIntl, defineMessages } from 'react-intl';

import { withGrowl } from '../../components/GrowlNotifications';
import { parse, sortUrl, toSortOrder } from '../../utils/UrlUtil';
import Paginator from '../../components/Paginator';
import UserInvitationListToolbar from './UserInvitationListToolbar';
import {
  growlShape, intlShape, historyShape, locationShape,
} from '../../components/prop-types';

const messages = defineMessages({
  success: { id: 'noti.success', defaultMessage: 'Success!' },
  fail: { id: 'noti.fail', defaultMessage: 'Failed!' },
  delete: { id: 'actions.delete', defaultMessage: 'Delete' },
  sendInvitation: { id: 'actions.sennd-invitation', defaultMessage: 'Send Invitation' },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  email: { id: 'attrs.email', defaultMessage: 'Email' },
  role: { id: 'attrs.role', defaultMessage: 'Role' },
  createdOn: { id: 'attrs.created.verbose', defaultMessage: 'Created on' },
  invitedOn: { id: 'attrs.invited.verbose', defaultMessage: 'Invited on' },
  administrator: { id: 'attrs.role.administrator', defaultMessage: 'Administrator' },
  externalSupport: { id: 'attrs.role.external-support', defaultMessage: 'External Support' },
  user: { id: 'attrs.role.user', defaultMessage: 'User' },
});

const createdTemplate = (rowData, column) => {
  const created = column.formatDate(moment(rowData.createdOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return created;
};

const roleTemplate = (rowData, column) => {
  const { role: { name } } = rowData;
  const { formatMessage } = column;

  switch (name) {
    case 'admin':
    case 'nbm-admin':
      return formatMessage(messages.administrator);
    case 'external-support':
    case 'nbm-external-support':
      return formatMessage(messages.externalSupport);
    case 'member':
    case 'nbm-member':
      return formatMessage(messages.user);
    default:
      return '';
  }
};

const invitedTemplate = (rowData, column) => {
  const updated = column.formatDate(moment(rowData.invitedOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

class UserInvitationList extends Component {
  constructor(props) {
    super(props);
    this.onSortChange = this.onSortChange.bind(this);
    this.actionsTemplate = this.actionsTemplate.bind(this);
    this.openConetextMenu = this.openConetextMenu.bind(this);
    this.sendInvitation = this.sendInvitation.bind(this);
    this.cancelInvitation = this.cancelInvitation.bind(this);
  }

  state = {
    menu: [],
  };

  onSortChange(event) {
    const { location, history } = this.props;
    sortUrl(event, location, history);
  }

  actionsTemplate(rowData) {
    return (
      <Button icon="pi pi-ellipsis-h" className="p-button-secondary" onClick={e => this.openConetextMenu(e, rowData)} />
    );
  }

  async sendInvitation(id) {
    const {
      sendInvitation,
      currentAccount,
      growl,
      intl: { formatMessage },
    } = this.props;
    // toggleLoading(true)
    await sendInvitation.mutation({
      variables: {
        input: {
          account: currentAccount,
          invitation: id,
        },
      },
    });
    growl.show({
      severity: 'info',
      detail: formatMessage(messages.success),
    });
    // toggleLoading(false)
  }

  async cancelInvitation(id) {
    const {
      cancelInvitation,
      currentAccount,
      growl,
      intl: { formatMessage },
    } = this.props;
    // toggleLoading(true)
    await cancelInvitation.mutation({
      variables: {
        input: {
          account: currentAccount,
          invitation: id,
        },
      },
    });
    growl.show({
      severity: 'info',
      detail: formatMessage(messages.success),
    });
    // toggleLoading(false)
  }

  buildMenu(rowData) {
    const {
      intl: { formatMessage },
    } = this.props;

    return [
      {
        label: formatMessage(messages.sendInvitation),
        icon: 'fa fa-paper-plane',
        command: () => this.sendInvitation(rowData.id),
      },
      {
        label: formatMessage(messages.delete),
        icon: 'pi pi-times',
        command: () => this.cancelInvitation(rowData.id),
      },
    ];
  }

  openConetextMenu(event, rowData) {
    this.setState({
      menu: this.buildMenu(rowData),
    });
    this.menu.toggle(event);
  }

  render() {
    const {
      connection: {
        edges, totalCount, indexFrom, indexTo, pageInfo,
      },
      intl: { formatMessage, formatDate },
      currentAccount,
      location,
      loading,
    } = this.props;
    const connectionRows = edges.map(({ node }) => ({
      ...node,
    }));

    const params = parse(location.search);
    const { sort = {} } = params;
    const { sortBy, sortDir } = sort;

    const nameHeader = formatMessage(messages.name);
    const emailHeader = formatMessage(messages.email);
    const roleHeader = formatMessage(messages.role);
    const createdHeader = formatMessage(messages.createdOn);
    const invitedHeader = formatMessage(messages.invitedOn);

    const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

    const { menu } = this.state;
    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel>
              <UserInvitationListToolbar currentAccount={currentAccount} />
              <div className="p-grid">
                <div className="p-col-12 align-center">
                  <Menu
                    model={menu}
                    popup
                    ref={(el) => {
                      this.menu = el;
                      return el;
                    }}
                    appendTo={document.body}
                  />

                  <DataTable
                    value={connectionRows}
                    responsive
                    sortField={sortBy}
                    sortOrder={toSortOrder(sortDir)}
                    onSort={this.onSortChange}
                    lazy
                    loading={loading}
                    autoLayout
                  >
                    <Column field="fullName" header={nameHeader} sortable style={{ textAlign: 'left' }} />
                    <Column field="email" header={emailHeader} sortable />
                    <Column
                      field="role"
                      header={roleHeader}
                      body={roleTemplate}
                      currentAccount={currentAccount}
                      formatMessage={formatMessage}
                      sortable
                    />
                    <Column
                      field="createdOn"
                      header={createdHeader}
                      body={createdTemplate}
                      formatDate={formatDate}
                      sortable
                    />
                    <Column
                      field="invitedOn"
                      header={invitedHeader}
                      body={invitedTemplate}
                      formatDate={formatDate}
                      sortable
                    />
                    <Column body={this.actionsTemplate} style={{ width: '3.5em' }} />
                  </DataTable>
                </div>
                <div className="p-col-12 align-center">{footer}</div>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
}

UserInvitationList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: intlShape.isRequired,
  growl: growlShape.isRequired,
  currentAccount: PropTypes.string.isRequired,
  location: locationShape.isRequired,
  history: historyShape.isRequired,
  loading: PropTypes.bool.isRequired,
  sendInvitation: PropTypes.shape({
    mutation: PropTypes.func.isRequired,
    result: PropTypes.object.isRequired,
  }).isRequired,
  cancelInvitation: PropTypes.shape({
    mutation: PropTypes.func.isRequired,
    result: PropTypes.object.isRequired,
  }).isRequired,
};

export default withRouter(injectIntl(withGrowl(UserInvitationList)));
